import React, { useState, useRef, useEffect } from "react";

/**
 * OptimizedImage - A component that provides LQIP (Low Quality Image Placeholder)
 * and WebP support for CMS images.
 * 
 * Features:
 * - Shows LQIP placeholder while the full image loads (blur effect)
 * - Prefers WebP format when available, falls back to original src
 * - Smooth fade-in transition when full image loads
 * - Skips placeholder if lqip is not available
 * 
 * @param {string} src - The original image source URL
 * @param {string} webpSrc - Optional WebP source URL (preferred if browser supports)
 * @param {string} lqipSrc - Optional Low Quality Image Placeholder URL
 * @param {string} alt - Alt text for accessibility
 * @param {string} className - CSS classes to apply
 * @param {object} style - Inline styles to apply
 * @param {function} onLoad - Callback when full image loads
 * @param {function} onError - Callback on error
 * @param {object} rest - Any other props to pass to the img element
 */
const OptimizedImage = ({
    src,
    webpSrc,
    lqipSrc,
    alt = "",
    className = "",
    style = {},
    onLoad,
    onError,
    loading: loadingProp = "lazy",
    fetchPriority,
    ...rest
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [currentSrc, setCurrentSrc] = useState(null);
    const imgRef = useRef(null);

    // Determine the best source to use
    useEffect(() => {
        // Reset state when src changes
        setIsLoaded(false);
        setHasError(false);

        // Prefer WebP if available, otherwise fallback to original src
        const finalSrc = webpSrc || src;
        setCurrentSrc(finalSrc);
    }, [src, webpSrc]);

    const handleLoad = (e) => {
        setIsLoaded(true);
        if (onLoad) onLoad(e);
    };

    const handleError = (e) => {
        // If WebP failed, try falling back to original src
        if (webpSrc && currentSrc === webpSrc && src) {
            console.warn("[OptimizedImage] WebP failed, falling back to:", src);
            setCurrentSrc(src);
        } else {
            setHasError(true);
            if (onError) onError(e);
        }
    };

    // If no src at all, don't render
    if (!src && !webpSrc) {
        return null;
    }

    const containerStyle = {
        position: "relative",
        overflow: "hidden",
        ...style,
    };

    const lqipStyle = {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        filter: "blur(10px)",
        transform: "scale(1.1)", // Prevent blur edges
        transition: "opacity 0.3s ease-out",
        opacity: isLoaded ? 0 : 1,
        zIndex: 1,
    };

    const mainImageStyle = {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        transition: "opacity 0.3s ease-in",
        opacity: isLoaded ? 1 : 0,
        zIndex: 2,
        position: "relative",
    };

    return (
        <div style={containerStyle} className={className}>
            {/* LQIP placeholder - only show if lqipSrc is provided and image not yet loaded */}
            {lqipSrc && !hasError && !isLoaded && (
                <img
                    src={lqipSrc}
                    alt=""
                    aria-hidden="true"
                    style={lqipStyle}
                    loading="eager"
                />
            )}

            {/* Main image */}
            {currentSrc && (
                <img
                    ref={imgRef}
                    src={currentSrc}
                    alt={alt}
                    style={mainImageStyle}
                    onLoad={handleLoad}
                    onError={handleError}
                    loading={loadingProp}
                    fetchPriority={fetchPriority}
                    {...rest}
                />
            )}
        </div>
    );
};

/**
 * OptimizedBackgroundImage - Same as OptimizedImage but for background images
 * Used for elements that need backgroundImage style
 */
export const OptimizedBackgroundImage = ({
    src,
    webpSrc,
    lqipSrc,
    children,
    className = "",
    style = {},
    ...rest
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [currentSrc, setCurrentSrc] = useState(null);
    const imageRef = useRef(null);

    useEffect(() => {
        // Reset loaded state when sources change
        setIsLoaded(false);

        const finalSrc = webpSrc || src;
        if (!finalSrc) return;

        setCurrentSrc(finalSrc);

        // Preload the image using Image constructor
        var img = new Image();

        img.onload = function () {
            setIsLoaded(true);
        };

        img.onerror = function () {
            // Fallback to original src if webp fails
            if (webpSrc && finalSrc === webpSrc && src) {
                console.warn("[OptimizedBackgroundImage] WebP failed, falling back to:", src);
                setCurrentSrc(src);
                var fallbackImg = new Image();
                fallbackImg.onload = function () {
                    setIsLoaded(true);
                };
                fallbackImg.src = src;
            } else {
                // Even on error, hide LQIP after timeout to prevent stuck state
                setTimeout(function () {
                    setIsLoaded(true);
                }, 3000);
            }
        };

        img.src = finalSrc;
        imageRef.current = img;

        return function () {
            // Cleanup
            if (imageRef.current) {
                imageRef.current.onload = null;
                imageRef.current.onerror = null;
            }
        };
    }, [src, webpSrc]);

    var containerStyle = {
        position: "relative",
        overflow: "hidden",
    };
    // Merge with passed style
    Object.keys(style).forEach(function (key) {
        containerStyle[key] = style[key];
    });

    var lqipBgStyle = {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: lqipSrc ? "url(" + lqipSrc + ")" : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        WebkitFilter: "blur(10px)",
        filter: "blur(10px)",
        WebkitTransform: "scale(1.1)",
        transform: "scale(1.1)",
        WebkitTransition: "opacity 0.4s ease-out",
        transition: "opacity 0.4s ease-out",
        opacity: isLoaded ? 0 : 1,
        visibility: isLoaded ? "hidden" : "visible",
        zIndex: 1,
        pointerEvents: "none",
    };

    var mainBgStyle = {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: currentSrc ? "url(" + currentSrc + ")" : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        WebkitTransition: "opacity 0.4s ease-in",
        transition: "opacity 0.4s ease-in",
        opacity: isLoaded ? 1 : 0,
        zIndex: 2,
        pointerEvents: "none",
    };

    if (!src && !webpSrc) {
        return null;
    }

    return (
        <div style={containerStyle} className={className} {...rest}>
            {/* LQIP layer - shown while main image loads, removed from DOM after load */}
            {lqipSrc && !isLoaded && <div style={lqipBgStyle} />}
            {/* Main image layer */}
            <div style={mainBgStyle} />
            {/* Content layer */}
            <div style={{ position: "relative", zIndex: 3, width: "100%", height: "100%" }}>
                {children}
            </div>
        </div>
    );
};

export default OptimizedImage;
